/**
 * PayablesAnalysisPage
 *
 * Análise completa de contas a pagar — layout alinhado ao Dashboard Financeiro.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowDownRight,
  AlertTriangle,
  Receipt,
  Users,
  CheckCircle2,
  Calendar as CalendarIcon,
  RotateCcw,
  PieChart,
  BarChart3
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import { formatDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import { payablesAnalysisService } from '../../../../services/payablesAnalysisService'
import KPICard from '../../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import DonutChart from '../../../../components/ERP/FinanceDashboard/DonutChart/DonutChart'
import PayablesMonthlyBarChart from '../../../../components/ERP/FinanceDashboard/PayablesMonthlyBarChart/PayablesMonthlyBarChart'
import PayablesMonthlyList from '../../../../components/ERP/FinanceDashboard/PayablesMonthlyList/PayablesMonthlyList'
import '../Dashboard/FinanceDashboardPage.css'
import './PayablesAnalysisPage.css'

function getDefaultDateRange() {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth()
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  return {
    from: toDateInputValue(first),
    to: toDateInputValue(last)
  }
}

function buildPeriodLabel(from, to) {
  if (!from || !to) return 'Selecionar período'
  return `${formatDateOnly(from)} — ${formatDateOnly(to)}`
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function PayablesAnalysisPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const periodBtnRef = useRef(null)

  const initialRange = useMemo(() => {
    const state = location.state || {}
    if (state.dateFrom && state.dateTo) {
      return { from: state.dateFrom, to: state.dateTo }
    }
    if (state.dueDateFrom && state.dueDateTo) {
      return { from: state.dueDateFrom, to: state.dueDateTo }
    }
    return getDefaultDateRange()
  }, [location.state])

  const initialPreset = location.state?.preset || location.state?.dashboardPreset || null

  const [dateRange, setDateRange] = useState(initialRange)
  const [preset, setPreset] = useState(initialPreset)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [analysisData, setAnalysisData] = useState(null)
  const [error, setError] = useState(null)
  const hasDisplayedDataRef = useRef(false)

  const periodLabel = useMemo(
    () => buildPeriodLabel(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  )

  const loadAnalysis = useCallback(async () => {
    if (hasDisplayedDataRef.current) {
      setRefreshing(true)
    } else {
      setInitialLoading(true)
    }
    setError(null)

    try {
      const params = { preset: preset || undefined }
      if (preset !== 'overdue') {
        params.dateFrom = dateRange.from
        params.dateTo = dateRange.to
      }

      const response = await payablesAnalysisService.getAnalysis(params)

      if (response.success) {
        setAnalysisData(response.data)
        if (preset === 'overdue' && response.data?.period?.dateFrom && response.data?.period?.dateTo) {
          setDateRange({
            from: response.data.period.dateFrom,
            to: response.data.period.dateTo
          })
        }
        hasDisplayedDataRef.current = true
      } else {
        setError('Erro ao carregar análise de contas a pagar')
      }
    } catch (err) {
      console.error('Erro ao carregar análise de contas a pagar:', err)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      setInitialLoading(false)
      setRefreshing(false)
    }
  }, [dateRange.from, dateRange.to, preset])

  useEffect(() => {
    loadAnalysis()
  }, [loadAnalysis])

  const handlePeriodRangeChange = (from, to) => {
    if (!from || !to) return
    setDateRange({ from, to })
    setPreset(null)
  }

  const handleClearPeriodFilter = () => {
    setDateRange(getDefaultDateRange())
    setPreset(null)
  }

  const kpis = analysisData?.kpis
  const isBusy = initialLoading || refreshing

  const kpiCards = kpis ? [
    {
      label: 'Total no período',
      value: formatCurrency(kpis.totalDue?.amount),
      change: `${kpis.totalDue?.count || 0} títulos`,
      up: false,
      icon: Receipt,
      color: 'text-rose-500',
      bg: 'bg-rose-50'
    },
    {
      label: 'Em aberto',
      value: formatCurrency(kpis.open?.amount),
      change: `${kpis.open?.count || 0} títulos`,
      up: false,
      icon: ArrowDownRight,
      color: 'text-orange-500',
      bg: 'bg-orange-50'
    },
    {
      label: 'Vencidas',
      value: formatCurrency(kpis.overdue?.amount),
      change: `${kpis.overdue?.count || 0} títulos`,
      up: false,
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50'
    },
    {
      label: 'Pagas',
      value: formatCurrency(kpis.paid?.amount),
      change: `${kpis.paid?.count || 0} títulos`,
      up: true,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Fornecedores',
      value: String(kpis.suppliers?.count || 0),
      change: 'distintos no período',
      up: true,
      icon: Users,
      color: 'text-indigo-500',
      bg: 'bg-indigo-50'
    }
  ] : []

  const openListagem = (listPreset = null) => {
    navigate('/erp/financeiro/contas-pagar', {
      state: {
        dashboardPreset: listPreset || preset || 'dateRange',
        dueDateFrom: dateRange.from,
        dueDateTo: dateRange.to
      }
    })
  }

  if (initialLoading && !analysisData) {
    return (
      <div className="finance-dashboard-loading">
        <p>Carregando análise de contas a pagar...</p>
      </div>
    )
  }

  if (error && !analysisData) {
    return (
      <div className="finance-dashboard-error">
        <p>{error}</p>
        <button type="button" onClick={loadAnalysis}>Tentar novamente</button>
      </div>
    )
  }

  return (
    <div className={`finance-dashboard payables-analysis-page${refreshing ? ' is-refreshing' : ''}`}>
      <div className="finance-dashboard-header">
        <div className="payables-analysis-header-main">
          <button
            type="button"
            className="payables-analysis-back-btn"
            onClick={() => navigate('/erp/financeiro/dashboard')}
            aria-label="Voltar ao dashboard financeiro"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="finance-dashboard-title">
              Análise de <span className="finance-dashboard-title-highlight">Contas a Pagar</span>
              {refreshing && (
                <RotateCcw size={20} className="finance-dashboard-refresh-icon" aria-hidden />
              )}
            </h1>
            <p className="finance-dashboard-subtitle">
              Compromissos, vencimentos e pagamentos por período
              {preset === 'overdue' && ' — somente vencidas'}
            </p>
          </div>
        </div>
        <div className="finance-dashboard-header-actions">
          <button
            type="button"
            onClick={loadAnalysis}
            className="finance-dashboard-reload-btn"
            disabled={isBusy}
            aria-label="Atualizar análise"
            title="Atualizar dados"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'finance-dashboard-reload-icon is-spinning' : 'finance-dashboard-reload-icon'}
            />
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive"
            title="Filtrar período por vencimento"
          >
            <CalendarIcon size={14} className="finance-dashboard-period-icon" />
            <span>{periodLabel}</span>
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={dateRange.from}
            endValue={dateRange.to}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Período da análise"
            subtitle="Filtrar por data de vencimento"
          />
        </div>
      </div>

      {error && (
        <div className="finance-dashboard-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadAnalysis}>Tentar novamente</button>
        </div>
      )}

      <div className="finance-dashboard-shortcuts-section">
        <h3 className="finance-dashboard-section-title">Ações</h3>
        <div className="finance-dashboard-shortcuts-grid">
          <button
            type="button"
            onClick={() => openListagem()}
            className="finance-dashboard-shortcut-btn"
          >
            <div className="finance-dashboard-shortcut-icon bg-rose-600">
              <Receipt size={22} />
            </div>
            <span className="finance-dashboard-shortcut-label">Abrir listagem</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPreset(preset === 'overdue' ? null : 'overdue')
            }}
            className="finance-dashboard-shortcut-btn"
          >
            <div className="finance-dashboard-shortcut-icon bg-amber-600">
              <AlertTriangle size={22} />
            </div>
            <span className="finance-dashboard-shortcut-label">
              {preset === 'overdue' ? 'Ver todas no período' : 'Somente vencidas'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/erp/financeiro/dashboard')}
            className="finance-dashboard-shortcut-btn"
          >
            <div className="finance-dashboard-shortcut-icon bg-slate-900">
              <BarChart3 size={22} />
            </div>
            <span className="finance-dashboard-shortcut-label">Dashboard financeiro</span>
          </button>
        </div>
      </div>

      <div className="finance-dashboard-dynamic">
        <div className="finance-dashboard-kpi-grid payables-analysis-kpi-grid">
          {kpiCards.map((kpi, i) => (
            <KPICard key={i} {...kpi} />
          ))}
        </div>

        <div className="payables-analysis-charts-row">
          <PayablesMonthlyBarChart data={analysisData?.monthlySeries} />

          {analysisData?.byCategory?.length > 0 && (
            <div className="cash-flow-analysis-donut-chart-card finance-dashboard-opex-donut payables-analysis-donut">
              <h3 className="cash-flow-analysis-donut-chart-title">
                <PieChart size={18} /> Por categoria
              </h3>
              <DonutChart data={analysisData.byCategory} legendMode="floating" />
            </div>
          )}
        </div>

        <PayablesMonthlyList
          months={analysisData?.monthlyList || []}
          onItemClick={() => openListagem()}
        />
      </div>
    </div>
  )
}

export default PayablesAnalysisPage
